import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { AppointmentSubjectResponseSubjectTypeEnum as SubjectType } from '@dopingtech/live_expert_bff_api_ts';
import { Dropdown, Modal } from '@/components';
import { STUDENT_AVATAR_TONE_COUNT } from '../../boutique-classroom.constants';
import { ClassroomGroup, PlannedLessonDraft, PlannedLessonItem, PlannedLessonMessage, PlannedLessonSlot, SelectItem } from '../../boutique-classroom.types';
import { BoutiqueStudent } from '../../students/boutique-students.types';
import { useCreatePlannedLessons, useMeetingSubjects } from '../../hooks';
import PlannedLessonDateModal from './PlannedLessonDateModal';
import {
	PLANNED_LESSON_LONG_DATE_FORMAT,
	PLANNED_LESSON_SHORT_DATE_FORMAT,
	PLANNED_LESSON_STEPS,
	PLANNED_LESSON_TEXT,
	PLANNED_LESSON_WEEKDAY_FORMAT,
	PlannedLessonStep
} from './planned-lesson-modal.constants';
import { formatDate, getBoardExperts, getBranchOptions, getDurationMinutes, getExpertOptions, getInitials, toSlotKey } from './planned-lesson-modal.utils';
import '@/assets/styles/views/boutique-classroom/create-group-modal.styles.scss';
import '@/assets/styles/views/boutique-classroom/planned-lesson-modal.styles.scss';

type PlannedLessonStepValue = (typeof PlannedLessonStep)[keyof typeof PlannedLessonStep];

interface PlannedLessonModalProps {
	isOpen: boolean;
	students: BoutiqueStudent[];
	groups: ClassroomGroup[];
	onRemoveStudent: (studentId: number) => void;
	onClose: () => void;
	onFinish: () => void;
}

const EMPTY_DRAFT: PlannedLessonDraft = { branchId: null, lesson: null, unit: null, topic: null, expertId: null, date: null, slot: null };

function isComplete(draft: PlannedLessonDraft): boolean {
	return draft.branchId !== null && draft.topic !== null && draft.expertId !== null && draft.date !== null && draft.slot !== null;
}

function getStudentMeta(student: BoutiqueStudent): string {
	return [student.gradeLabel, student.groupName].filter(Boolean).join(' · ');
}

function findOption(options: SelectItem[], id: number | null): SelectItem | null {
	return options.find(option => option.id === id) ?? null;
}

interface SubjectFieldProps {
	label: string;
	studentId: number;
	subjectType: Parameters<typeof useMeetingSubjects>[1];
	parentId?: SelectItem['id'];
	branchId?: number | null;
	selected: SelectItem | null;
	onSelect: (option: SelectItem) => void;
}

const SubjectField: React.FC<SubjectFieldProps> = ({ label, studentId, subjectType, parentId, branchId, selected, onSelect }) => {
	const options = useMeetingSubjects(studentId, subjectType, parentId, branchId);
	return (
		<div className="plm-row__field">
			<span className="plm-row__label">{label}</span>
			<Dropdown<SelectItem>
				options={options}
				selectedOption={selected}
				onOptionSelect={onSelect}
				placeholder={PLANNED_LESSON_TEXT.SELECT_PLACEHOLDER}
				size="small"
				fullWidth
				disabled={subjectType === SubjectType.Lesson ? !branchId : parentId === undefined}
			/>
		</div>
	);
};

const StudentAvatar: React.FC<{ student: { id: number; fullName: string } }> = ({ student }) => (
	<span className={clsx('cgm-student__avatar', `cgm-student__avatar--tone-${student.id % STUDENT_AVATAR_TONE_COUNT}`)}>{getInitials(student.fullName)}</span>
);

export const PlannedLessonModal: React.FC<PlannedLessonModalProps> = ({ isOpen, students, groups, onRemoveStudent, onClose, onFinish }) => {
	const [step, setStep] = useState<PlannedLessonStepValue>(PlannedLessonStep.STUDENTS);
	const [drafts, setDrafts] = useState<Record<number, PlannedLessonDraft>>({});
	const [dateStudentId, setDateStudentId] = useState<number | null>(null);
	const [openStudentId, setOpenStudentId] = useState<number | null | undefined>(undefined);
	const [createdLessons, setCreatedLessons] = useState<PlannedLessonItem[]>([]);
	const [errors, setErrors] = useState<Record<number, PlannedLessonMessage>>({});
	const { createPlannedLessons, isCreating } = useCreatePlannedLessons();
	const isSuccess = step === PlannedLessonStep.SUCCESS;

	const reset = useCallback(() => {
		setStep(PlannedLessonStep.STUDENTS);
		setDrafts({});
		setOpenStudentId(undefined);
		setCreatedLessons([]);
		setErrors({});
	}, []);

	useEffect(() => {
		if (isOpen) reset();
	}, [isOpen, reset]);

	const boardExperts = useMemo(() => Object.fromEntries(students.map(student => [student.id, getBoardExperts(student, groups)])), [groups, students]);
	const getDraft = useCallback((studentId: number) => drafts[studentId] ?? EMPTY_DRAFT, [drafts]);

	const clearError = useCallback((studentId: number) => {
		setErrors(prev => {
			if (!prev[studentId]) return prev;
			const next = { ...prev };
			delete next[studentId];
			return next;
		});
	}, []);

	const updateDraft = useCallback((studentId: number, patch: Partial<PlannedLessonDraft>) => {
		clearError(studentId);
		setDrafts(prev => ({ ...prev, [studentId]: { ...(prev[studentId] ?? EMPTY_DRAFT), ...patch } }));
	}, [clearError]);

	const handleBranchChange = useCallback(
		(studentId: number, branchId: number) => {
			const draft = getDraft(studentId);
			const expertOptions = getExpertOptions(boardExperts[studentId] ?? [], branchId);
			const keepExpert = expertOptions.some(option => option.id === draft.expertId);
			const expertId = keepExpert ? draft.expertId : expertOptions.length === 1 ? (expertOptions[0].id as number) : null;
			const subjects = branchId === draft.branchId ? {} : { lesson: null, unit: null, topic: null };
			updateDraft(studentId, expertId === draft.expertId ? { branchId, ...subjects } : { branchId, ...subjects, expertId, date: null, slot: null });
		},
		[boardExperts, getDraft, updateDraft]
	);

	const handleLessonChange = useCallback(
		(studentId: number, lesson: SelectItem) => {
			if (getDraft(studentId).lesson?.id !== lesson.id) updateDraft(studentId, { lesson, unit: null, topic: null });
		},
		[getDraft, updateDraft]
	);

	const handleUnitChange = useCallback(
		(studentId: number, unit: SelectItem) => {
			if (getDraft(studentId).unit?.id !== unit.id) updateDraft(studentId, { unit, topic: null });
		},
		[getDraft, updateDraft]
	);

	const handleExpertChange = useCallback(
		(studentId: number, expertId: number) => {
			if (getDraft(studentId).expertId !== expertId) updateDraft(studentId, { expertId, date: null, slot: null });
		},
		[getDraft, updateDraft]
	);

	const handleRemove = useCallback(
		(studentId: number) => {
			if (students.length <= 1) return;
			clearError(studentId);
			onRemoveStudent(studentId);
		},
		[clearError, onRemoveStudent, students.length]
	);

	const lessons = useMemo<PlannedLessonItem[]>(
		() =>
			students.flatMap(student => {
				const draft = getDraft(student.id);
				if (!isComplete(draft)) return [];
				const experts = boardExperts[student.id] ?? [];
				return [
					{
						studentId: student.id,
						studentName: student.fullName,
						gradeId: student.gradeId,
						branchId: draft.branchId as number,
						branchName: findOption(getBranchOptions(experts), draft.branchId)?.name ?? '',
						lessonId: draft.lesson?.id as number,
						lessonName: draft.lesson?.name ?? '',
						topicId: draft.topic?.id as number,
						topicName: draft.topic?.name ?? '',
						expertId: draft.expertId as number,
						expertName: findOption(getExpertOptions(experts, draft.branchId), draft.expertId)?.name ?? '',
						date: draft.date as string,
						slot: draft.slot as PlannedLessonSlot
					}
				];
			}),
		[boardExperts, getDraft, students]
	);
	const allComplete = students.length > 0 && lessons.length === students.length;
	const hasErrors = students.some(student => errors[student.id]);

	const dateStudent = students.find(student => student.id === dateStudentId) ?? null;
	const dateDraft = dateStudent ? getDraft(dateStudent.id) : null;
	const takenSlotKeys = useMemo(
		() =>
			dateDraft
				? students
						.filter(student => student.id !== dateStudentId)
						.map(student => getDraft(student.id))
						.filter(draft => draft.expertId === dateDraft.expertId && draft.date && draft.slot)
						.map(draft => toSlotKey(draft.date as string, (draft.slot as PlannedLessonSlot).id))
				: [],
		[dateDraft, dateStudentId, getDraft, students]
	);
	const dateSubtitle =
		dateStudent && dateDraft ? [getStudentMeta(dateStudent), findOption(getBranchOptions(boardExperts[dateStudent.id] ?? []), dateDraft.branchId)?.name].filter(Boolean).join(' · ') : '';

	const handleDateSelect = useCallback(
		(date: string, slot: PlannedLessonSlot) => {
			if (dateStudentId !== null) updateDraft(dateStudentId, { date, slot });
			setDateStudentId(null);
		},
		[dateStudentId, updateDraft]
	);

	const handleCreate = useCallback(async () => {
		if (isCreating || !allComplete) return;
		setErrors({});
		const result = await createPlannedLessons(lessons);
		if (!result) return;
		if (result.createdStudentIds.length === lessons.length) {
			setCreatedLessons(lessons);
			setStep(PlannedLessonStep.SUCCESS);
			return;
		}
		result.createdStudentIds.forEach(onRemoveStudent);
		setErrors(result.errors);
	}, [allComplete, createPlannedLessons, isCreating, lessons, onRemoveStudent]);

	const renderSteps = () => (
		<ol className="plm-steps">
			{PLANNED_LESSON_STEPS.map(item => (
				<li
					key={item.id}
					className={clsx('plm-steps__item', { 'plm-steps__item--active': step === item.id, 'plm-steps__item--done': step > item.id })}>
					<span className="plm-steps__index">{step > item.id ? <i className="icons-pure-check" /> : item.id}</span>
					<span className="plm-steps__label">{item.label}</span>
				</li>
			))}
		</ol>
	);

	const renderStudents = () => (
		<>
			<div className="cgm-field">
				<span className="cgm-field__label plm-section-title">
					{PLANNED_LESSON_TEXT.SELECTED_STUDENTS} ({students.length})
				</span>
				<ul className="cgm-students">
					{students.map(student => (
						<li
							key={student.id}
							className="cgm-student">
							<StudentAvatar student={student} />
							<span className="cgm-student__text">
								<span className="cgm-student__name">{student.fullName}</span>
								<span className="cgm-student__meta">{getStudentMeta(student)}</span>
							</span>
							<button
								type="button"
								className="cgm-student__remove"
								aria-label={`${student.fullName} öğrencisini listeden çıkar`}
								disabled={students.length <= 1}
								onClick={() => handleRemove(student.id)}>
								<i className="icons-close" />
							</button>
						</li>
					))}
				</ul>
			</div>
			<div className="cgm-info">
				<i className="icons-info-circle" />
				<span>{PLANNED_LESSON_TEXT.STUDENTS_INFO}</span>
			</div>
		</>
	);

	const renderDetails = () => {
		const expandedStudentId = openStudentId === undefined ? students[0]?.id : openStudentId;
		return (
			<>
				<p className="cgm__description">{PLANNED_LESSON_TEXT.DETAILS_DESCRIPTION}</p>
				<ul className="plm-rows">
					{students.map(student => {
						const draft = getDraft(student.id);
						const experts = boardExperts[student.id] ?? [];
						const branchOptions = getBranchOptions(experts);
						const expertOptions = getExpertOptions(experts, draft.branchId);
						const isExpanded = expandedStudentId === student.id;
						return (
							<li
								key={student.id}
								className={clsx('plm-row', { 'plm-row--open': isExpanded })}>
								<div className="plm-row__student">
									<StudentAvatar student={student} />
									<span className="cgm-student__text">
										<span className="cgm-student__name">{student.fullName}</span>
										<span className="cgm-student__meta">{getStudentMeta(student)}</span>
									</span>
									{!isExpanded && <span className="plm-row__summary">{[findOption(branchOptions, draft.branchId)?.name, draft.lesson?.name].filter(Boolean).join(' • ')}</span>}
									<button
										type="button"
										className="plm-row__toggle"
										aria-label={PLANNED_LESSON_TEXT.TOGGLE_DETAILS}
										aria-expanded={isExpanded}
										onClick={() => setOpenStudentId(isExpanded ? null : student.id)}>
										<i className={isExpanded ? 'icons-alt-arrow-up' : 'icons-alt-arrow-down'} />
									</button>
									<button
										type="button"
										className="cgm-student__remove plm-row__remove"
										aria-label={`${student.fullName} öğrencisini listeden çıkar`}
										disabled={students.length <= 1}
										onClick={() => handleRemove(student.id)}>
										<i className="icons-close" />
									</button>
								</div>
								<div className="plm-row__fields">
									<div className="plm-row__field">
										<span className="plm-row__label">{PLANNED_LESSON_TEXT.BRANCH_LABEL}</span>
										<Dropdown<SelectItem>
											options={branchOptions}
											selectedOption={findOption(branchOptions, draft.branchId)}
											onOptionSelect={option => handleBranchChange(student.id, option.id as number)}
											placeholder={PLANNED_LESSON_TEXT.SELECT_PLACEHOLDER}
											size="small"
											fullWidth
										/>
									</div>
									<SubjectField
										label={PLANNED_LESSON_TEXT.LESSON_LABEL}
										studentId={student.id}
										subjectType={SubjectType.Lesson}
										branchId={draft.branchId}
										selected={draft.lesson}
										onSelect={option => handleLessonChange(student.id, option)}
									/>
									<SubjectField
										label={PLANNED_LESSON_TEXT.UNIT_LABEL}
										studentId={student.id}
										subjectType={SubjectType.Unit}
										parentId={draft.lesson?.id}
										selected={draft.unit}
										onSelect={option => handleUnitChange(student.id, option)}
									/>
									<SubjectField
										label={PLANNED_LESSON_TEXT.TOPIC_LABEL}
										studentId={student.id}
										subjectType={SubjectType.Subject}
										parentId={draft.unit?.id}
										selected={draft.topic}
										onSelect={topic => updateDraft(student.id, { topic })}
									/>
									<div className="plm-row__field">
										<span className="plm-row__label">{PLANNED_LESSON_TEXT.EXPERT_LABEL}</span>
										<Dropdown<SelectItem>
											options={expertOptions}
											selectedOption={findOption(expertOptions, draft.expertId)}
											onOptionSelect={option => handleExpertChange(student.id, option.id as number)}
											placeholder={PLANNED_LESSON_TEXT.SELECT_PLACEHOLDER}
											size="small"
											fullWidth
											disabled={draft.branchId === null}
										/>
									</div>
									<div className="plm-row__field">
										<span className="plm-row__label">{PLANNED_LESSON_TEXT.DATE_TIME_LABEL}</span>
										<button
											type="button"
											className={clsx('plm-row__date', { 'plm-row__date--empty': !draft.slot })}
											disabled={draft.expertId === null}
											onClick={() => setDateStudentId(student.id)}>
											<i className="icons-calendar" />
											{draft.date && draft.slot ? `${formatDate(draft.date, PLANNED_LESSON_SHORT_DATE_FORMAT)}, ${draft.slot.startTime}` : PLANNED_LESSON_TEXT.DATE_TIME_PLACEHOLDER}
										</button>
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			</>
		);
	};

	const renderSummary = () => (
		<>
			<span className="cgm-field__label plm-section-title">
				{PLANNED_LESSON_TEXT.SUMMARY_TITLE} ({lessons.length})
			</span>
			<ul className="plm-summary">
				{lessons.map(lesson => {
					const student = students.find(item => item.id === lesson.studentId);
					const error = errors[lesson.studentId];
					return (
						<li
							key={lesson.studentId}
							className={clsx('plm-summary__card', { 'plm-summary__card--error': error })}>
							<StudentAvatar student={{ id: lesson.studentId, fullName: lesson.studentName }} />
							<div className="plm-summary__body">
								<span className="cgm-student__name">{lesson.studentName}</span>
								{student && <span className="cgm-student__meta">{getStudentMeta(student)}</span>}
								<div className="plm-summary__meta">
									<span>
										<i className="icons-checklist-minimalistic" />
										{lesson.branchName}
									</span>
									<span>
										<i className="icons-document" />
										{lesson.lessonName} · {lesson.topicName}
									</span>
									<span>
										<i className="icons-user" />
										{lesson.expertName}
									</span>
								</div>
								<div className="plm-summary__meta">
									<span>
										<i className="icons-calendar" />
										{formatDate(lesson.date, PLANNED_LESSON_LONG_DATE_FORMAT)} {formatDate(lesson.date, PLANNED_LESSON_WEEKDAY_FORMAT)}
									</span>
									<span>
										<i className="icons-clock-circle" />
										{lesson.slot.startTime}
									</span>
									<span>
										<i className="icons-clock-circle" />
										{getDurationMinutes(lesson.slot.startTime, lesson.slot.endTime)} {PLANNED_LESSON_TEXT.MINUTES_SUFFIX}
									</span>
								</div>
								{error && <span className="plm-summary__error">{error.message}</span>}
							</div>
							<div className="plm-summary__actions">
								<button
									type="button"
									className="plm-summary__edit"
									aria-label={`${lesson.studentName} dersini düzenle`}
									disabled={isCreating}
									onClick={() => setStep(PlannedLessonStep.DETAILS)}>
									<i className="icons-pen" />
								</button>
								<button
									type="button"
									className="cgm-student__remove"
									aria-label={`${lesson.studentName} öğrencisini listeden çıkar`}
									disabled={isCreating || students.length <= 1}
									onClick={() => handleRemove(lesson.studentId)}>
									<i className="icons-trash-bin-minimalistic" />
								</button>
							</div>
						</li>
					);
				})}
			</ul>
		</>
	);

	const renderSuccess = () => (
		<div className="plm-success">
			<span className="plm-success__icon">
				<i className="icons-pure-check" />
			</span>
			<h3 className="plm-success__title">{PLANNED_LESSON_TEXT.SUCCESS_TITLE}</h3>
			<p className="plm-success__description">
				{createdLessons.length} {PLANNED_LESSON_TEXT.SUCCESS_DESCRIPTION_SUFFIX}
			</p>
			<ul className="plm-success__list">
				{createdLessons.map(lesson => (
					<li
						key={lesson.studentId}
						className="plm-success__item">
						<StudentAvatar student={{ id: lesson.studentId, fullName: lesson.studentName }} />
						<span className="cgm-student__name">{lesson.studentName}</span>
						<span className="plm-success__detail">
							<span>
								{formatDate(lesson.date, PLANNED_LESSON_SHORT_DATE_FORMAT)}, {lesson.slot.startTime}
							</span>
							<span className="cgm-student__meta">
								{lesson.branchName} · {lesson.expertName}
							</span>
						</span>
					</li>
				))}
			</ul>
		</div>
	);

	const renderFooter = () => {
		switch (step) {
			case PlannedLessonStep.STUDENTS:
				return (
					<>
						<button
							type="button"
							className="cgm-button cgm-button--secondary"
							onClick={onClose}>
							{PLANNED_LESSON_TEXT.CANCEL}
						</button>
						<button
							type="button"
							className="cgm-button cgm-button--primary"
							disabled={!students.length}
							onClick={() => setStep(PlannedLessonStep.DETAILS)}>
							{PLANNED_LESSON_TEXT.CONTINUE}
						</button>
					</>
				);
			case PlannedLessonStep.DETAILS:
				return (
					<>
						<button
							type="button"
							className="cgm-button cgm-button--secondary"
							onClick={() => setStep(PlannedLessonStep.STUDENTS)}>
							{PLANNED_LESSON_TEXT.BACK}
						</button>
						<button
							type="button"
							className="cgm-button cgm-button--primary"
							disabled={!allComplete}
							onClick={() => setStep(PlannedLessonStep.SUMMARY)}>
							{PLANNED_LESSON_TEXT.CONTINUE}
						</button>
					</>
				);
			case PlannedLessonStep.SUMMARY:
				return (
					<>
						<button
							type="button"
							className="cgm-button cgm-button--secondary"
							disabled={isCreating}
							onClick={() => setStep(PlannedLessonStep.DETAILS)}>
							{PLANNED_LESSON_TEXT.BACK}
						</button>
						<button
							type="button"
							className="cgm-button cgm-button--primary"
							disabled={isCreating || !allComplete || hasErrors}
							aria-busy={isCreating}
							onClick={handleCreate}>
							{isCreating ? PLANNED_LESSON_TEXT.CREATING : `${lessons.length} ${PLANNED_LESSON_TEXT.CREATE_SUFFIX}`}
						</button>
					</>
				);
			default:
				return (
					<>
						<button
							type="button"
							className="cgm-button cgm-button--secondary"
							onClick={reset}>
							{PLANNED_LESSON_TEXT.PLAN_ANOTHER}
						</button>
						<button
							type="button"
							className="cgm-button cgm-button--primary"
							onClick={onFinish}>
							{PLANNED_LESSON_TEXT.DONE}
						</button>
					</>
				);
		}
	};

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={isSuccess ? onFinish : onClose}
				hasHeader
				title={isSuccess ? '' : PLANNED_LESSON_TEXT.TITLE}
				showCloseButton
				size="lg"
				variant="centered"
				closeOnClickOutside={false}
				closeOnEsc={dateStudentId === null && !isCreating}
				className={clsx('cgm-modal plm-modal', { 'plm-modal--wide': step === PlannedLessonStep.DETAILS })}
				contentClassName="cgm-modal__content"
				headerClassName="cgm-modal__header"
				bodyClassName="cgm-modal__body">
				<div className="cgm">
					<div className="cgm__scroll">
						{!isSuccess && (
							<>
								<p className="cgm__description">{PLANNED_LESSON_TEXT.DESCRIPTION}</p>
								{renderSteps()}
							</>
						)}
						{step === PlannedLessonStep.STUDENTS && renderStudents()}
						{step === PlannedLessonStep.DETAILS && renderDetails()}
						{step === PlannedLessonStep.SUMMARY && renderSummary()}
						{isSuccess && renderSuccess()}
					</div>
					<div className="cgm__footer">{renderFooter()}</div>
				</div>
			</Modal>
			<PlannedLessonDateModal
				student={dateStudent}
				subtitle={dateSubtitle}
				draft={dateDraft}
				takenSlotKeys={takenSlotKeys}
				onClose={() => setDateStudentId(null)}
				onSelect={handleDateSelect}
			/>
		</>
	);
};

export default PlannedLessonModal;
